import { useTranslation } from 'react-i18next';

import { EmptyDataMessage, NgButtonNavLink } from '@/ui';
import { incidentArchiveRoute } from '@/features/shared/routes';
import type { IncidentListRow } from '@/features/shared/incidentListRow';
import { IncidentListComponent } from '@/features/shared/IncidentListComponent';
import { IncidentListLoadingPlaceholder } from '@/features/shared/IncidentListLoadingPlaceholder';

export type IncidentListPageListProps = {
  forwardedIncidents: readonly IncidentListRow[];
  currentlyOpenIncidents: readonly IncidentListRow[];
  recentClosedIncidents: readonly IncidentListRow[];
  isLoading?: boolean;
};

export function IncidentListPageList({
  forwardedIncidents,
  currentlyOpenIncidents,
  recentClosedIncidents,
  isLoading = false,
}: IncidentListPageListProps) {
  const { t } = useTranslation();

  return (
    <>
      {forwardedIncidents.length > 0 && (
        <div className="p-2 pt-2 mb-2 bg-info-subtle">
          <h2 className="d-flex align-items-center">
            {t('IncidentList_Heading_CollaborationRequestedIncidents')}
            <span className="badge text-bg-info text-white ms-2 px-2 py-1">{forwardedIncidents.length}</span>
          </h2>

          <IncidentListComponent incidents={forwardedIncidents} />
        </div>
      )}

      <div className="p-2 pt-2 bg-warning-subtle">
        <h2 className="d-flex align-items-center">
          {t('IncidentList_Heading_OpenIncidents')}
          {currentlyOpenIncidents.length > 0 && (
            <span className="badge text-bg-warning ms-2 px-2 py-1">{currentlyOpenIncidents.length}</span>
          )}
        </h2>

        {isLoading ? (
          <IncidentListLoadingPlaceholder />
        ) : currentlyOpenIncidents.length > 0 ? (
          <IncidentListComponent incidents={currentlyOpenIncidents} />
        ) : (
          <EmptyDataMessage text={t('IncidentList_NoOpenIncidentsNote')} />
        )}
      </div>

      <div className="p-2 mt-2">
        <h2>{t('IncidentList_Heading_RecentIncidents')}</h2>

        {isLoading ? (
          <IncidentListLoadingPlaceholder />
        ) : recentClosedIncidents.length > 0 ? (
          <IncidentListComponent incidents={recentClosedIncidents} />
        ) : (
          <>
            <EmptyDataMessage text={t('IncidentList_NoRecentIncidentsNote_AllIncidentsOlderThan12Hours')} />

            <div className="d-flex justify-content-center">
              <NgButtonNavLink to={incidentArchiveRoute()} text={t('Button_GoToIncidentArchive')} outline />
            </div>
          </>
        )}
      </div>
    </>
  );
}
